import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Card } from '~/client/components/ui/card'
import { Badge } from '~/client/components/ui/badge'
import ImageCarousel from '~/client/components/ImageCarousel'
import { AttachmentItem } from '~/client/components/AttachmentLightbox'
import { AttachmentIcon as Paperclip } from '~/client/components/icons'
import { useEventMembershipLinks } from '~/client/inquiry/use-event-membership-links.hook'
import AttachmentLightbox from '~/client/components/AttachmentLightbox'

// Route handles both /event/:eventId/:membershipId/links and /event/:eventSlug/:membershipId/links
// The $eventId param can be either a numeric ID or a string slug
export const Route = createFileRoute('/event/$eventId/$membershipId/links')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: RouteComponent,
})

function AttachmentChip({ item, onClick }: { item: AttachmentItem; onClick: () => void }) {
  return (
    <Badge variant="chip" className="bg-white px-[8px] py-[8px] border-2 border-[#8a3dff]">
      <button
        onClick={onClick}
        className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
        type="button"
      >
        <AttachmentChipContent item={item} />
      </button>
    </Badge>
  )
}

function AttachmentChipContent({ item }: { item: AttachmentItem }) {
  return (
    <div className="flex items-center gap-[8px]">
      <div className="overflow-clip relative shrink-0 size-[18px]">
        <Paperclip className="block max-w-none size-full grayscale" />
      </div>
      <div className="flex items-center gap-[4px] text-[12px] leading-[16px]">
        <div className="font-['Inter:Medium',_sans-serif] text-[#95919a] overflow-hidden text-ellipsis">{item.filename}</div>
      </div>
    </div>
  )
}

function AttachmentSection({ title, attachments, onSelectAttachment }: { title: string; attachments: AttachmentItem[]; onSelectAttachment: (item: AttachmentItem) => void }) {
  if (!attachments.length) return null

  return (
    <>
      <div className="flex flex-col gap-[12px]">
        <div className="text-[#95919a] text-xs tracking-[0.08em] uppercase mb-2">{title}</div>
        <div className="flex flex-wrap items-start gap-[12px]">
          {attachments.map((item, idx) => (
            <AttachmentChip key={`${item.label}-${idx}`} item={item} onClick={() => onSelectAttachment(item)} />
          ))}
        </div>
      </div>
    </>
  )
}

function RouteComponent() {
  const params = Route.useParams()
  const search = Route.useSearch()
  const { data, error, isLoading } = useEventMembershipLinks({
    eventId: params.eventId,
    membershipId: params.membershipId,
    token: search.token,
  })
  const eventName = data?.eventName

  const [lightboxOpen, setLightboxOpen] = React.useState(false)
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = React.useState(0)
  const [allAttachments, setAllAttachments] = React.useState<AttachmentItem[]>([])

  React.useEffect(() => {
    if (eventName) {
      document.title = `Revel Street - ${eventName} - Files`
    }
  }, [eventName])

  const handleSelectAttachment = React.useCallback((item: AttachmentItem) => {
    if (!data) return
    const combinedAttachments = [
      ...(data.spaceAttachments || []),
      ...(data.venueAttachments || []),
      ...(data.eventAttachments || [])
    ]
    const index = combinedAttachments.findIndex(att => att.href === item.href && att.filename === item.filename)
    if (index !== -1) {
      setAllAttachments(combinedAttachments)
      setSelectedAttachmentIndex(index)
      setLightboxOpen(true)
    }
  }, [data])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FEFBF6] flex items-center justify-center px-6">
        <Card className="max-w-lg w-full p-6 text-center">
          <div className="text-[#6a6262] text-lg font-semibold">Loading share link...</div>
        </Card>
      </div>
    )
  }

  const resolvedData = data || { error: error || 'Failed to load share link data.' }

  if (resolvedData.error) {
    return (
      <div className="min-h-screen bg-[#FEFBF6] flex items-center justify-center px-6">
        <Card className="max-w-lg w-full p-6 text-center">
          <div className="text-[#6a6262] text-lg font-semibold">{resolvedData.error}</div>
          <div className="text-[#9a9191] text-sm mt-2">Please request a new link from the sender.</div>
        </Card>
      </div>
    )
  }

  const images = resolvedData.images || []
  const hasAttachments =
    (resolvedData.spaceAttachments?.length || 0) + (resolvedData.venueAttachments?.length || 0) + (resolvedData.eventAttachments?.length || 0) > 0

  return (
    <div className="h-dvh min-h-screen bg-[#FEFBF6] px-6 py-8 overflow-y-auto">
      <div className="max-w-5xl mx-auto flex flex-col gap-12 ">
        <div>
          <div className="text-[#95919a] text-xs tracking-[0.08em] uppercase">Venue</div>
          <div className="text-[#322f36] text-2xl font-semibold mt-1">
            {resolvedData.venueName} - {resolvedData.spaceName}
          </div>
        </div>

        {images.length ? (
          <Card className="bg-transparent border-none shadow-none">
            <div className="text-[#95919a] text-xs tracking-[0.08em] uppercase mb-4">Photos</div>
            <div className="h-[424px] rounded-[8px] overflow-hidden">
              <ImageCarousel
                images={images}
                className="h-full w-full"
                title={`${resolvedData.spaceName}`}
              />
            </div>
          </Card>
        ) : null}

        {hasAttachments ? (
          <div className="flex flex-col gap-[36px] my-[12px] pb-[64px]">
            <AttachmentSection
              title="Attachments"
              attachments={[
                ...(resolvedData.spaceAttachments || []),
                ...(resolvedData.venueAttachments || []),
                ...(resolvedData.eventAttachments || [])
              ]}
              onSelectAttachment={handleSelectAttachment}
            />
          </div>
        ) : null}
      </div>

      <AttachmentLightbox
        attachments={allAttachments}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        selectedIndex={selectedAttachmentIndex}
        onSelectIndex={setSelectedAttachmentIndex}
      />
    </div>
  )
}
